/**
 * pdf-email-preview — the quote/invoice "send email" modal (PDF preview +
 * recipient resolution + manual/automatic send). Extracted from the inline
 * <script> in templates/components/pdf_email_preview_modal.html
 * (chippie-2s0pu.25) so the send flow lives in controller code.
 *
 * One controller instance per modal (quote detail hosts the quote modal,
 * invoice detail the invoice modal). The record id arrives at open time:
 *   * an outside trigger with data-action="open-email-modal" and
 *     data-entity-type / data-entity-id (document-level delegation, the
 *     gallery_controller 'trigger-photo-upload' pattern), or
 *   * a window CustomEvent 'pdf-email-preview:open' with
 *     detail {entityType, id} (for JS callers, e.g. quote_detail.js), or
 *   * arriving at the page with #send in the address (the send prompt's
 *     bottom card links to a quote that way), which opens the modal for the
 *     page's own Send trigger.
 *
 * A send the server accepts is announced as a window CustomEvent
 * 'pdf-email-preview:handed-off' with detail {entityType, id}.
 *
 * PDF.js is lazy-loaded on first open via a window-cached promise shared by
 * both modals. Recipient logic mirrors the server's resolution
 * (InvoiceService._send_invoice_email); the server stays authoritative.
 */
import { Controller } from '@hotwired/stimulus';
import { resendConfirmLink } from './_resend_link.js';

/** The address fragment that asks a detail page to open its send modal; the
 *  send prompt's link names it too (onboard_send in macros/onboarding.html). */
const SEND_INTENT = '#send';

/** Load the PDF.js asset once for send modals and walkthrough previews. */
export function ensurePdfJs(src) {
  if (window.pdfjsLib) return Promise.resolve();
  if (window.__pdfJsAssetPromise) return window.__pdfJsAssetPromise;
  window.__pdfJsAssetPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = () => {
      window.__pdfJsAssetPromise = null;
      script.remove();
      reject(new Error('Failed to load PDF.js'));
    };
    document.head.appendChild(script);
  });
  return window.__pdfJsAssetPromise;
}

export default class extends Controller {
  static values = {
    entityType: String,
    pdfjsSrc: String,
    newTabFn: String,
    downloadFn: String,
  };

  static targets = [
    'canvasContainer', 'canvas', 'loading', 'error', 'pageInfo',
    'prevBtn', 'nextBtn', 'clientInfo', 'picker', 'pickerSelect',
    'verifyPrompt', 'verifyMessage', 'autoBtn', 'photoNote', 'photoNoteText',
  ];

  connect() {
    this._emailData = null;
    this._currentId = null;
    this._pdfDoc = null;
    this._pageNum = 1;
    this._pageIsRendering = false;
    this._pageNumPending = null;
    this._renderTask = null;
    this._scale = 1.2;

    this._onTriggerClick = this._handleTriggerClick.bind(this);
    document.addEventListener('click', this._onTriggerClick);
    this._onOpenEvent = this._handleOpenEvent.bind(this);
    window.addEventListener('pdf-email-preview:open', this._onOpenEvent);
    this._openForSendIntent();
  }

  disconnect() {
    this._resetPaging();
    document.removeEventListener('click', this._onTriggerClick);
    window.removeEventListener('pdf-email-preview:open', this._onOpenEvent);
  }

  // ── Entry points ──────────────────────────────────────────────────────────

  _handleTriggerClick(event) {
    const el = event.target.closest('[data-action="open-email-modal"]');
    if (!el) return;
    if (el.dataset.entityType !== this.entityTypeValue) return;
    event.preventDefault();
    this.open(el.dataset.entityId);
  }

  _handleOpenEvent(event) {
    const detail = event.detail || {};
    if (detail.entityType !== this.entityTypeValue) return;
    this.open(detail.id);
  }

  // Opened at #send: open for the page's own Send trigger. Not on Turbo's
  // cached preview, whose body the real render replaces; and the fragment is
  // dropped first, so a later reconnect (a restore visit, a page-check swap)
  // never opens the modal again.
  _openForSendIntent() {
    if (window.location.hash !== SEND_INTENT) return;
    if (document.documentElement.hasAttribute('data-turbo-preview')) return;
    const trigger = document.querySelector(
      `[data-action="open-email-modal"][data-entity-type="${this.entityTypeValue}"]`);
    if (!trigger) return;
    history.replaceState(history.state, '', window.location.pathname + window.location.search);
    this.open(trigger.dataset.entityId);
  }

  open(entityId) {
    this._currentId = entityId;
    bootstrap.Modal.getOrCreateInstance(this.element).show();
    this._renderPhotoNote(null);
    this._loadPdfPreview(entityId);
    this._loadEmailData(entityId);
  }

  // ── Email data + recipient resolution ────────────────────────────────────

  _loadEmailData(entityId) {
    window.api(`/${this.entityTypeValue}/${entityId}/email-data`, { notifyError: false })
      .then((data) => {
        if (data && data.success) {
          this._emailData = data.data;
          this._populateContactPicker(data.data);
          this._renderRecipientInfo();
          this._updateSendGuard();
          this._renderPhotoNote(data.data.photo_note);
        } else {
          console.error(`Error loading ${this.entityTypeValue} email data:`, data && data.error);
          this._renderClientInfoError();
        }
      })
      .catch((error) => {
        console.error(`Error loading ${this.entityTypeValue} email data:`, error);
        this._renderClientInfoError();
      });
  }

  // The server's note that the client would see none of the record's photos
  // (ClientPhotoService.hidden_photos_note), or null to hide it. Advisory only:
  // it never disables a send.
  _renderPhotoNote(note) {
    if (!this.hasPhotoNoteTarget) return;
    this.photoNoteTextTarget.textContent = note || '';
    this.photoNoteTarget.classList.toggle('d-none', !note);
  }

  // "Choose photos": close the modal and bring the record's Photos card, where
  // each photo carries its Show to client tick, into view.
  showPhotos() {
    bootstrap.Modal.getOrCreateInstance(this.element).hide();
    const card = document.getElementById(`${this.entityTypeValue}-${this._currentId}-photos`);
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _renderClientInfoError() {
    if (!this.hasClientInfoTarget) return;
    this.clientInfoTarget.innerHTML = `
        <div class="text-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>
            Unable to load client information
        </div>
    `;
  }

  // Populate the recipient-contact picker only when the client has additional
  // contacts. The empty option sends to the client primary; default selection
  // is the persisted choice, else the primary contact.
  _populateContactPicker(data) {
    if (!this.hasPickerTarget || !this.hasPickerSelectTarget) return;
    const contacts = data.contacts || [];
    if (contacts.length === 0) {
      this.pickerTarget.style.display = 'none';
      return;
    }
    const primary = contacts.find((c) => c.is_primary);
    const currentId = data.recipient_contact_id;
    const defaultId = (currentId != null) ? currentId : (primary ? primary.id : '');

    // Every option names a real contact. There used to be a leading
    // value="" option meaning "no explicit recipient — send to the client's
    // default contact", labelled with the CLIENT's name. But the default
    // contact IS the primary contact, which is also listed below, so the same
    // person appeared twice under two different names — and the name the
    // fallback wore exists nowhere else, so it read as a phantom extra primary
    // that no client page could show. Selecting the primary by id sends to the
    // same address and says who it is going to.
    let options = primary
      ? ''
      : `<option value="">${data.client_name} (no default contact)</option>`;
    contacts.forEach((c) => {
      const label = c.display_name + (c.email ? ' (' + c.email + ')' : '');
      const selected = (String(c.id) === String(defaultId)) ? ' selected' : '';
      options += `<option value="${c.id}"${selected}>${label}</option>`;
    });
    this.pickerSelectTarget.innerHTML = options;
    this.pickerTarget.style.display = 'block';
  }

  /**
   * Re-label the picker's options from the contacts in hand, without touching
   * the selection (repopulating would reset it to the persisted choice).
   *
   * Saving an email used to refresh only the panel above, so the dropdown kept
   * showing the address the contact had when the page loaded — the same person
   * listed with two different emails on one screen.
   */
  _refreshContactLabels() {
    if (!this.hasPickerSelectTarget) return;
    const contacts = (this._emailData && this._emailData.contacts) || [];
    Array.from(this.pickerSelectTarget.options).forEach((opt) => {
      const c = contacts.find((x) => String(x.id) === String(opt.value));
      if (c) opt.textContent = c.display_name + (c.email ? ` (${c.email})` : '');
    });
  }

  // Changing the addressed contact changes the resolved recipient email, so
  // re-render the recipient panel and re-check the guard. (change action on
  // the select.)
  onRecipientChange() {
    this._renderRecipientInfo();
    this._updateSendGuard();
  }

  /**
   * The contact "Address to" currently names, or null when the send falls back
   * to the client's primary.
   *
   * Sole owner of that question: the panel, the send guard and the save all
   * have to agree on WHO this is going to, and the bug this replaced came from
   * two of them answering it differently — the panel resolved the addressed
   * contact's email while the save wrote to the client's default contact, so
   * an email typed for Dana landed on Jim.
   */
  _addressedContact() {
    const data = this._emailData;
    if (!data) return null;
    if (this.hasPickerTarget && this.hasPickerSelectTarget
        && this.pickerTarget.style.display !== 'none' && this.pickerSelectTarget.value) {
      return (data.contacts || []).find(
        (c) => String(c.id) === String(this.pickerSelectTarget.value),
      ) || null;
    }
    return null;
  }

  /**
   * Resolve the email the automatic send will actually go to: the chosen
   * additional contact's email when the picker is showing one, else the
   * client's primary email. Mirrors InvoiceService._send_invoice_email.
   */
  _resolveRecipientEmail() {
    const data = this._emailData;
    if (!data) return '';
    const chosen = this._addressedContact();
    if (chosen) return (chosen.email || '').trim();
    return (data.client_email || '').trim();
  }

  /**
   * Render the Recipient panel. With a resolvable email, show it. Without one,
   * show a red warning plus an inline "Add email address" affordance that
   * writes the email to the client (POST /client/<id>/set-email) without
   * leaving the send. The inserted markup wires back into this controller via
   * data-action attributes (Stimulus binds actions on inserted nodes).
   */
  _renderRecipientInfo() {
    const data = this._emailData;
    if (!data || !this.hasClientInfoTarget) return;
    const email = this._resolveRecipientEmail();
    // Name whoever the send is actually addressed to. Showing the client while
    // "Address to" named someone else is what made "no email address on file"
    // read as the CLIENT's missing email, so the address typed in went to the
    // client's default contact in the user's mind as well as in the code.
    const chosen = this._addressedContact();
    const recipientName = chosen ? chosen.display_name : data.client_name;
    const phoneRow = data.client_phone ? `<div class="small text-muted">${data.client_phone}</div>` : '';

    if (email) {
      this.clientInfoTarget.innerHTML = `
        <div class="d-flex align-items-center">
            <div class="flex-grow-1">
                <div class="fw-bold">${recipientName}</div>
                <div class="small text-muted">${email}</div>
                ${phoneRow}
            </div>
        </div>`;
      return;
    }

    this.clientInfoTarget.innerHTML = `
        <div class="flex-grow-1">
            <div class="fw-bold">${recipientName}</div>
            <div class="small text-danger d-flex align-items-start mt-1">
                <i class="bi bi-exclamation-triangle-fill me-1 mt-1"></i>
                <span>No email address on file — add one to send this ${this.entityTypeValue} automatically.</span>
            </div>
            ${phoneRow}
            <button type="button" class="btn btn-sm btn-outline-primary mt-2"
                    data-add-email-btn data-action="pdf-email-preview#showAddEmailForm">
                <i class="bi bi-plus-circle me-1"></i>Add email address
            </button>
            <div data-add-email-form class="mt-2" style="display: none;">
                <div class="input-group input-group-sm">
                    <input type="email" data-add-email-input class="form-control"
                           placeholder="name@example.com" autocomplete="email"
                           inputmode="email" autocapitalize="off" autocorrect="off"
                           data-action="keydown->pdf-email-preview#onAddEmailKeydown">
                    <button type="button" class="btn btn-primary" data-add-email-save
                            data-action="pdf-email-preview#saveAddedEmail">Save</button>
                </div>
                <div data-add-email-error class="small text-danger mt-1" style="display: none;"></div>
            </div>
        </div>`;
  }

  /**
   * Enable/disable the Send Automatically button on whether a recipient email
   * exists. No email → disabled (dulled) with an explanatory title.
   */
  _updateSendGuard() {
    if (!this.hasAutoBtnTarget) return;
    const autoBtn = this.autoBtnTarget;
    if (this._resolveRecipientEmail()) {
      autoBtn.disabled = false;
      autoBtn.classList.remove('disabled');
      autoBtn.removeAttribute('title');
    } else {
      autoBtn.disabled = true;
      autoBtn.classList.add('disabled');
      autoBtn.title = 'Add an email address for this client to send automatically.';
    }
  }

  showAddEmailForm(event) {
    const form = this.clientInfoTarget.querySelector('[data-add-email-form]');
    if (form) form.style.display = 'block';
    event.currentTarget.style.display = 'none';
    const input = this.clientInfoTarget.querySelector('[data-add-email-input]');
    if (input) input.focus();
  }

  onAddEmailKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveAddedEmail();
    }
  }

  /**
   * Write the typed address to whoever the send is addressed to.
   *
   * With a contact named in "Address to" that is the CONTACT's email, not the
   * client's: this used to POST /client/<id>/set-email unconditionally, so an
   * address entered for the addressed contact was saved onto the client's
   * default contact instead — the send still had no recipient email, and the
   * default contact silently acquired someone else's address.
   *
   * Resolves to the saved address; rejects with a message to show inline.
   */
  _persistRecipientEmail(email) {
    const contact = this._addressedContact();
    if (contact) {
      // acceptStream: false — this endpoint answers a client page with a Turbo
      // Stream for the contacts section, which is not on this page.
      return window.contactApi
        .update(contact.id, { email }, { acceptStream: false })
        .then(() => {
          contact.email = email;
          return email;
        });
    }
    return window
      .api(`/client/${this._emailData.client_id}/set-email`, {
        method: 'POST',
        body: { email },
        notifyError: false,
        throwOnError: false,
      })
      .then((resp) => {
        if (!resp || !resp.success) {
          throw new Error((resp && resp.error) || 'Could not save the email address.');
        }
        this._emailData.client_email = resp.email || email;
        return this._emailData.client_email;
      });
  }

  /**
   * Persist a newly entered recipient email, then re-render the recipient panel
   * and unblock the send. Server validates the address authoritatively.
   */
  saveAddedEmail() {
    const data = this._emailData;
    const input = this.clientInfoTarget.querySelector('[data-add-email-input]');
    const errEl = this.clientInfoTarget.querySelector('[data-add-email-error]');
    if (!data || !input) return;
    const email = (input.value || '').trim();
    const showErr = (msg) => {
      if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; }
    };
    if (errEl) errEl.style.display = 'none';

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showErr('Enter a valid email address.');
      return;
    }
    // Only the client fallback needs a client id; an addressed contact is
    // written by its own id.
    if (!data.client_id && !this._addressedContact()) {
      showErr(`Could not resolve the recipient for this ${this.entityTypeValue}.`);
      return;
    }

    const saveBtn = this.clientInfoTarget.querySelector('[data-add-email-save]');
    if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }

    this._persistRecipientEmail(email)
      .then(() => {
        this._renderRecipientInfo();
        this._refreshContactLabels();
        this._updateSendGuard();
        if (typeof showToast === 'function') showToast('Email address saved.', 'success');
      })
      .catch((err) => {
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
        showErr((err && err.message) || 'Could not save the email address. Please try again.');
      });
  }

  // ── Sending ───────────────────────────────────────────────────────────────

  sendManual() {
    if (!this._emailData || !this._currentId) {
      alert('Email data not loaded. Please try again.');
      return;
    }

    // Download PDF first.
    const pdfUrl = `/${this.entityTypeValue}/${this._currentId}/pdf?t=${Date.now()}`;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${this.entityTypeValue}_${this._currentId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Wait a moment for the PDF to download, then open the email client.
    setTimeout(() => {
      const mailtoUrl = this._emailData.mailto_url;
      try {
        window.open(mailtoUrl);
        this._hideModal();
        showToast('📎 PDF downloaded and email client opened! The PDF has been saved to your Downloads folder - please attach it to the email before sending.', 'success');
      } catch (error) {
        console.error('Error sending email:', error);
        this._hideModal();
        showToast('📎 PDF downloaded successfully! Your email client could not be opened automatically. Please compose the email manually and attach the downloaded PDF.', 'warning');
      }
    }, 500);
  }

  /**
   * Queue the send as a PowerSync intent row instead of POSTing (offline
   * path, chippie-2s0pu.7). Consults the per-operation registry — send is in
   * the local-first safe set as data, not a hardcoded decision here. The row
   * uploads through the normal connector flow on reconnect; the server runs
   * the send gates at apply time and enqueues the durable email outbox. The
   * row's presence is the "queued" signal; it vanishes on the sync iteration
   * after a successful upload.
   */
  _shouldQueueOffline() {
    return !navigator.onLine
      && !!window.chippiePowerSync
      && typeof window.isLocalFirstOperation === 'function'
      && window.isLocalFirstOperation(this.entityTypeValue, 'send');
  }

  async _queueSendIntent() {
    // Double-tap guard: the offline path skips the online path's spinner
    // rewrite, so disable the button for the duration of the queue write.
    const button = this.hasAutoBtnTarget ? this.autoBtnTarget : null;
    if (button) {
      if (button.disabled) return;
      button.disabled = true;
    }
    const contactId = (this.hasPickerTarget && this.hasPickerSelectTarget
        && this.pickerTarget.style.display !== 'none')
      ? (this.pickerSelectTarget.value || null)
      : null;
    try {
      // Idempotency: each insert is a distinct CrudEntry with its own
      // client_op_id, so the server-side dedup key cannot collapse a
      // re-queue — a pending row for this entity means it's already queued.
      const pending = await window.chippiePowerSync.getOptional(
        'SELECT id FROM powersync_send_requests WHERE entity_type = ? AND entity_id = ?',
        [this.entityTypeValue, String(this._currentId)],
      );
      if (pending) {
        this._hideModal();
        showToast('Already queued — will send when back online', 'info');
        return;
      }
      await window.chippiePowerSync.execute(
        "INSERT INTO powersync_send_requests (id, tenant_id, entity_type, entity_id, contact_id, requested_at) "
        + "VALUES (uuid(), ?, ?, ?, ?, datetime('now'))",
        [document.body.dataset.tenantId || '', this.entityTypeValue,
         String(this._currentId), contactId],
      );
      this._hideModal();
      showToast('Queued — will send when back online', 'info');
    } catch (error) {
      console.error('[pdf-email-preview] failed to queue send intent:', error);
      showErrorMessage('Could not queue this send. Try again when back online.');
    } finally {
      if (button) button.disabled = false;
    }
  }

  sendAuto() {
    if (this._shouldQueueOffline()) {
      this._queueSendIntent();
      return;
    }

    if (!this._emailData || !this._currentId) {
      alert('Email data not loaded. Please try again.');
      return;
    }

    // Guard: never dispatch a send with no recipient email. The button is also
    // disabled in this state; this covers keyboard/programmatic activation.
    if (!this._resolveRecipientEmail()) {
      showErrorMessage('This client has no email address. Add one in the Recipient section above, then send.');
      return;
    }

    const button = this.autoBtnTarget;
    const originalContent = button.innerHTML;
    button.disabled = true;
    button.innerHTML = `
        <div class="d-flex align-items-center justify-content-center">
            <div class="spinner-border spinner-border-sm me-2" role="status">
                <span class="visually-hidden">Sending...</span>
            </div>
            <div class="text-start">
                <div class="fw-bold">Sending Email...</div>
                <small>Please wait</small>
            </div>
        </div>
    `;

    // Include contact_id only when the picker was shown so single-contact
    // clients keep the one-click send untouched.
    const sendBody = {
      to_email: this._emailData.client_email,
      subject: this._emailData.subject,
      body: this._emailData.body,
    };
    if (this.hasPickerTarget && this.hasPickerSelectTarget
        && this.pickerTarget.style.display !== 'none') {
      sendBody.contact_id = this.pickerSelectTarget.value;
    }

    window.api(`/${this.entityTypeValue}/${this._currentId}/send-email`, {
      method: 'POST',
      body: sendBody,
      notifyError: false,
      // Resolve 4xx to the parsed body so the else-branch shows the server's
      // reason (e.g. "Add a price...") instead of a generic catch message.
      throwOnError: false,
    })
      .then((data) => {
        button.disabled = false;
        button.innerHTML = originalContent;

        if (data && data.success) {
          this._hideModal();
          // The send runs in the background; this only acknowledges the
          // hand-off. The real 'sent' / 'send failed' result arrives moments
          // later as a Mercure toast (static/js/sse-entity-updates.js).
          showSuccessMessage((data && data.message) || `Sending ${this.entityTypeValue}...`);
          // The quote page's send card folds away now rather than sitting
          // under that toast until the 'sent' repaint (onboard_send_controller.js).
          window.dispatchEvent(new CustomEvent('pdf-email-preview:handed-off', {
            detail: { entityType: this.entityTypeValue, id: this._currentId },
          }));
        } else if (data && data.verification_required) {
          // Keep the modal open: the prompt and its resend button are inside
          // it, so the user recovers without losing the send they started.
          this._showVerificationPrompt(data.message);
        } else {
          showErrorMessage((data && data.message) || 'Failed to send email');
        }
      })
      .catch((error) => {
        console.error(`Error sending ${this.entityTypeValue} email:`, error);
        button.disabled = false;
        button.innerHTML = originalContent;
        showErrorMessage('Failed to send email. Please try again.');
      });
  }

  _hideModal() {
    const modal = bootstrap.Modal.getInstance(this.element);
    if (modal) modal.hide();
  }

  // ── Outbound verification recovery ───────────────────────────────────────

  _showVerificationPrompt(message) {
    if (!this.hasVerifyPromptTarget) return;
    this.verifyMessageTarget.textContent = message || 'Verify your email address before sending.';
    this.verifyPromptTarget.classList.remove('d-none');
    this.verifyPromptTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * Resend the verification link from inside the send modal, through the
   * request every confirm-link button shares (_resend_link.js), which takes
   * the address from the session rather than trusting one from the page.
   */
  async resendVerification(event) {
    const button = event.currentTarget;
    const originalContent = button.innerHTML;
    button.disabled = true;
    button.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Sending...';

    const { ok, message } = await resendConfirmLink();
    button.disabled = false;
    button.innerHTML = originalContent;
    if (ok) {
      showSuccessMessage(message);
    } else {
      showErrorMessage(message);
    }
  }

  // ── PDF preview (PDF.js) ─────────────────────────────────────────────────

  /**
   * Lazy-load PDF.js on first modal open rather than eagerly on every page
   * visit — most visits never open this modal. Cached on window so
   * concurrent/later opens (quote + invoice modals can share a page) reuse
   * the single in-flight/settled load.
   */
  _ensurePdfJs() {
    return ensurePdfJs(this.pdfjsSrcValue);
  }

  _loadPdfPreview(entityId) {
    // Paging state is per-open, but connect() only runs once per page visit, so
    // a render still in flight (or already failed) from the previous open would
    // otherwise carry its lock into this one and freeze the arrows.
    this._resetPaging();

    this.loadingTarget.style.display = 'block';
    this.canvasContainerTarget.style.display = 'none';
    this.errorTarget.style.display = 'none';

    const pdfUrl = `/${this.entityTypeValue}/${entityId}/pdf?t=${Date.now()}`;

    // One plain download, no byte ranges: the route renders the PDF afresh on
    // every request and the bytes differ each time, so ranges fetched by
    // follow-up requests do not line up with the first ("Bad end offset").
    const source = { url: pdfUrl, disableRange: true, disableStream: true };
    this._ensurePdfJs().then(() => pdfjsLib.getDocument(source).promise)
      .then((pdf) => {
        this._pdfDoc = pdf;
        this._pageNum = 1;
        this.pageInfoTarget.textContent = `Page 1 of ${pdf.numPages}`;
        this.prevBtnTarget.disabled = false;
        this.nextBtnTarget.disabled = pdf.numPages <= 1;

        this.loadingTarget.style.display = 'none';
        this.canvasContainerTarget.style.display = 'block';

        this._renderPage(this._pageNum);
      })
      .catch((error) => {
        console.error('Error loading PDF:', error);
        this.loadingTarget.style.display = 'none';
        this.errorTarget.style.display = 'block';
      });
  }

  /**
   * Drop any in-flight render and release the paging lock.
   *
   * _pageIsRendering gates every prev/next click through _queueRenderPage, so
   * it must never be left held: a render that rejects with the lock set turns
   * the arrows into no-ops for the rest of the page's life.
   */
  _resetPaging() {
    if (this._renderTask) {
      this._renderTask.cancel();
      this._renderTask = null;
    }
    this._pageIsRendering = false;
    this._pageNumPending = null;
  }

  /**
   * Release the lock and draw whatever the user asked for while we were busy.
   * Runs on both the success and failure paths of _renderPage.
   */
  _settleRender() {
    this._renderTask = null;
    this._pageIsRendering = false;

    const pending = this._pageNumPending;
    this._pageNumPending = null;
    if (pending !== null) this._renderPage(pending);
  }

  _renderPage(num) {
    // PDF.js throws if a second render() starts on a canvas the previous one is
    // still drawing to, and resizing the canvas below cancels an in-flight task
    // outright. Cancel first so the only rejection left is one we asked for.
    if (this._renderTask) this._renderTask.cancel();
    this._pageIsRendering = true;

    let task = null;
    this._pdfDoc.getPage(num)
      .then((page) => {
        const canvas = this.canvasTarget;
        const ctx = canvas.getContext('2d');
        const viewport = page.getViewport({ scale: this._scale });

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        task = page.render({ canvasContext: ctx, viewport });
        this._renderTask = task;
        return task.promise;
      })
      .then(() => {
        // A later _renderPage already owns the lock; settling here would clear
        // it out from under a render that is still running.
        if (this._renderTask === task) this._settleRender();
      })
      .catch((error) => {
        // Settle unless a different render is already live and owns the lock.
        if (this._renderTask !== task && this._renderTask !== null) return;
        this._settleRender();
        // Cancellation is the expected outcome of paging faster than a page
        // draws; anything else is a real failure worth seeing in the console.
        if (error && error.name !== 'RenderingCancelledException') {
          console.error('[pdf-email-preview] page render failed:', error);
        }
      });

    this.pageInfoTarget.textContent = `Page ${num} of ${this._pdfDoc.numPages}`;
  }

  _queueRenderPage(num) {
    if (this._pageIsRendering) {
      this._pageNumPending = num;
    } else {
      this._renderPage(num);
    }
  }

  prevPage() {
    if (this._pageNum <= 1) return;
    this._pageNum--;
    this._queueRenderPage(this._pageNum);
  }

  nextPage() {
    if (this._pageNum >= this._pdfDoc.numPages) return;
    this._pageNum++;
    this._queueRenderPage(this._pageNum);
  }

  zoomOut() {
    if (this._scale <= 0.5) return;
    this._scale -= 0.25;
    this._queueRenderPage(this._pageNum);
  }

  zoomIn() {
    if (this._scale >= 3) return;
    this._scale += 0.25;
    this._queueRenderPage(this._pageNum);
  }

  // Error-state fallbacks: delegate to the page's existing PDF helpers
  // (window.openPDFInNewTab / downloadQuotePDF and invoice equivalents),
  // whose names arrive via data-* values from the include site.
  openPdfNewTab() {
    this._callConfiguredFn(this.newTabFnValue);
  }

  downloadPdf() {
    this._callConfiguredFn(this.downloadFnValue);
  }

  _callConfiguredFn(name) {
    const fn = window[(name || '').replace(/^window\./, '')];
    if (typeof fn === 'function') fn(this._currentId);
  }
}
